import styled, { css, type DefaultTheme, RuleSet } from 'styled-components'

import { withBorder } from '@/theme/utils'

import { ButtonSizeKeys, ButtonUseKeys } from './button.types'

const buttonSizeStyles = (
  theme: DefaultTheme,
  $size: ButtonSizeKeys,
  $use: ButtonUseKeys,
  $onlyBox?: boolean,
) => {
  const hasBorder = $use === 'secondary' || $use === 'upsell-secondary'

  const sizeStyles: Record<ButtonSizeKeys, RuleSet> = {
    large: css`
      font-size: 1.6rem;
      line-height: 2.4rem;
      padding: ${withBorder(theme, 12, hasBorder)};

      ${
        !$onlyBox &&
        css`
        padding-inline: ${withBorder(theme, 24, hasBorder)};
      `
      }

      > [data-button-children-wrapper] > svg {
        width: 2.4rem;
      }
    `,
    medium: css`
      font-size: 1.4rem;
      line-height: 2rem;
      padding: ${withBorder(theme, 10, hasBorder)};

      ${
        !$onlyBox &&
        css`
        padding-inline: ${withBorder(theme, 20, hasBorder)};
      `
      }

      > [data-button-children-wrapper] > svg {
        width: 2rem;
      }
    `,
    small: css`
      font-size: 1.2rem;
      line-height: 1.6rem;
      padding: ${withBorder(theme, 8, hasBorder)};

      ${
        !$onlyBox &&
        css`
        padding-inline: ${withBorder(theme, 16, hasBorder)};
      `
      }

      > [data-button-children-wrapper] > svg {
        width: 1.6rem;
      }
    `,
    unset: css``,
  }

  return sizeStyles[$size]
}

export const buttonStyles = (
  theme: DefaultTheme,
  $use: ButtonUseKeys,
  $size: ButtonSizeKeys,
  $onlyBox?: boolean,
  $onlyCircle?: boolean,
  $overrideDisabled?: boolean,
  $compact?: boolean,
) => {
  const appearanceStyles: Record<ButtonUseKeys, RuleSet> = {
    ai: css`
      color: ${theme.colors.white};
      background:
        radial-gradient(286.58% 97.74% at 7.73% 5.67%, #b6a0fe 0%, rgba(182, 160, 254, 0) 100%),
        radial-gradient(287.49% 98.05% at 100% 100%, #63b8af 0%, rgba(99, 184, 175, 0) 100%),
        #0f87fc;
      border-radius: ${theme.border.radius.full};

      ${
        !$overrideDisabled &&
        css`
        &:hover,
        &:focus-visible {
          background:
            radial-gradient(286.58% 97.74% at 7.73% 5.67%, #b6a0fe 0%, rgba(182, 160, 254, 0) 100%),
            radial-gradient(287.49% 98.05% at 100% 100%, #63b8af 0%, rgba(99, 184, 175, 0) 100%),
            linear-gradient(0deg, #91dcff 0%, #91dcff 100%), #f2f9ff;
        }

        &:active,
        &[data-active='true'] {
          background:
            radial-gradient(286.58% 97.74% at 7.73% 5.67%, #b6a0fe 0%, rgba(182, 160, 254, 0) 100%),
            radial-gradient(287.49% 98.05% at 100% 100%, #63b8af 0%, rgba(99, 184, 175, 0) 100%),
            linear-gradient(0deg, #00236e 0%, #00236e 100%), #f2f9ff;
        }

        // NOTE: 'ai' doesn't have disabled styles
        &:disabled {
          background:
            radial-gradient(286.58% 97.74% at 7.73% 5.67%, #b6a0fe 0%, rgba(182, 160, 254, 0) 100%),
            radial-gradient(287.49% 98.05% at 100% 100%, #63b8af 0%, rgba(99, 184, 175, 0) 100%),
            #0f87fc;
          opacity: 0.5;
        }
      `
      }
    `,
    danger: css`
      color: ${theme.colors.red500};
      background: ${theme.colors.red100};
      border-radius: ${theme.border.radius['4']};

      ${
        !$overrideDisabled &&
        css`
        &:hover,
        &:focus-visible,
        &:active,
        &[data-active='true'] {
          background: ${theme.colors.red300};
        }

        &:disabled {
          background: ${theme.colors.red200};
          opacity: 0.5;
        }
      `
      }
    `,
    ghost: css`
      color: ${theme.colors.primary800};
      background: none;
      border-radius: ${theme.border.radius['4']};

      ${
        !$overrideDisabled &&
        css`
        &:hover,
        &:focus-visible {
          background: ${theme.colors.gray300};
        }

        &:active,
        &[data-active='true'] {
          background: ${theme.colors.primary200};
        }

        &:disabled {
          background: none;
          color: ${theme.colors.primary300};
        }
      `
      }
    `,
    ['ghost-error']: css`
      color: ${theme.colors.red500};
      background: none;
      border-radius: ${theme.border.radius['4']};

      ${
        !$overrideDisabled &&
        css`
        &:hover,
        &:focus-visible {
          background: ${theme.colors.red200};
        }

        &:active,
        &[data-active='true'] {
          background: ${theme.colors.red300};
        }

        &:disabled {
          background: none;
          color: ${theme.colors.red300};
        }
      `
      }
    `,
    ['ghost-light']: css`
      color: ${theme.colors.white};
      background: none;
      border-radius: ${theme.border.radius['4']};

      ${
        !$overrideDisabled &&
        css`
        &:hover,
        &:focus-visible {
          background: ${theme.colors.primary700};
        }

        &:active,
        &[data-active='true'] {
          background: ${theme.colors.primary800};
        }

        // NOTE: 'ghost-light' doesn't have disabled styles
        &:disabled {
          color: ${theme.colors.white};
          background: none;
          opacity: 0.5;
        }
      `
      }
    `,
    inverted: css`
      color: ${theme.colors.white};
      background: none;
      border-radius: ${theme.border.radius['4']};

      ${
        !$overrideDisabled &&
        css`
        &:hover,
        &:focus-visible {
          background: rgba(255, 255, 255, 0.1);
        }

        &:active,
        &[data-active='true'] {
          background: rgba(255, 255, 255, 0.2);
        }

        &:disabled {
          color: #ffffff80;
          background: none;
        }
      `
      }
    `,
    primary: css`
      color: ${theme.colors.white};
      background: ${theme.colors.black800};
      border-radius: ${theme.border.radius['4']};

      ${
        !$overrideDisabled &&
        css`
        &:hover,
        &:focus-visible {
          background: ${theme.colors.primary700};
        }

        &:active,
        &[data-active='true'] {
          background: ${theme.colors.black};
        }

        &:disabled {
          color: ${theme.colors.primary400};
          background: ${theme.colors.primary200};
        }
      `
      }
    `,
    ['primary-light']: css`
      color: ${theme.colors.primary800};
      background: ${theme.colors.white};
      border-radius: ${theme.border.radius['4']};

      ${
        !$overrideDisabled &&
        css`
        &:hover,
        &:focus-visible {
          background: ${theme.colors.primary200};
        }

        &:active,
        &[data-active='true'] {
          background: ${theme.colors.primary300};
        }

        // NOTE: 'primary-light' doesn't have disabled styles
        &:disabled {
          color: ${theme.colors.primary800};
          background: ${theme.colors.white};
          opacity: 0.5;
        }
      `
      }
    `,
    secondary: css`
      color: ${theme.colors.primary800};
      border: ${theme.border.size['1']} solid ${theme.colors.primary200};
      border-radius: ${theme.border.radius['4']};

      ${
        !$overrideDisabled &&
        css`
        &:hover,
        &:focus-visible {
          border: ${theme.border.size['1']} solid ${theme.colors.primary400};
        }

        &:active,
        &[data-active='true'] {
          border: ${theme.border.size['1']} solid ${theme.colors.primary800};
        }

        &:disabled {
          color: ${theme.colors.primary300};
          border: ${theme.border.size['1']} solid ${theme.colors.primary200};
        }
      `
      }
    `,
    success: css`
      color: ${theme.colors.green500};
      background: ${theme.colors.green100};
      border-radius: ${theme.border.radius['4']};

      ${
        !$overrideDisabled &&
        css`
        &:hover,
        &:focus-visible,
        &:active,
        &[data-active='true'] {
          background: ${theme.colors.green300};
        }

        &:disabled {
          background: ${theme.colors.green100};
          opacity: 0.5;
        }
      `
      }
    `,
    unset: css``,
    upsell: css`
      color: ${theme.colors.white};
      background: ${theme.colors.blue500};
      border-radius: ${theme.border.radius['4']};

      ${
        !$overrideDisabled &&
        css`
        &:hover,
        &:focus-visible {
          background: ${theme.colors.blue400};
        }

        &:active,
        &[data-active='true'] {
          background: ${theme.colors.blue600};
        }

        // NOTE: 'upsell' doesn't have disabled styles
        &:disabled {
          opacity: 0.5;
          background: ${theme.colors.blue500};
        }
      `
      }
    `,
    ['upsell-secondary']: css`
      color: ${theme.colors.blue500};
      background: ${theme.colors.blue100};
      border: ${theme.border.size['1']} solid ${theme.colors.blue500};
      border-radius: ${theme.border.radius['4']};

      ${
        !$overrideDisabled &&
        css`
        &:hover,
        &:focus-visible {
          background: ${theme.colors.blue200};
        }

        &:active,
        &[data-active='true'] {
          background: ${theme.colors.blue300};
        }

        // NOTE: 'upsell-secondary' doesn't have disabled styles
        &:disabled {
          opacity: 0.5;
          background: ${theme.colors.blue100};
        }
      `
      }
    `,
    warning: css`
      color: ${theme.colors.orange500};
      background: ${theme.colors.orange100};
      border-radius: ${theme.border.radius['4']};

      ${
        !$overrideDisabled &&
        css`
        &:hover,
        &:focus-visible,
        &:active,
        &[data-active='true'] {
          background: ${theme.colors.orange300};
        }

        &:disabled {
          background: ${theme.colors.orange100};
          opacity: 0.5;
        }
      `
      }
    `,
  }

  const onlyCircleStyles =
    $onlyCircle &&
    css`
      border-radius: ${theme.border.radius['circle']};
    `

  const onlyBoxSizeStyles: Record<ButtonSizeKeys, RuleSet> = {
    large: css`
      min-width: ${theme.space['48']};
    `,
    medium: css`
      min-width: ${theme.space['40']};
    `,
    small: css`
      min-width: ${theme.space['32']};
    `,
    unset: css``,
  }

  const onlyBoxStyles =
    $onlyBox &&
    css`
      ${onlyBoxSizeStyles[$size]}
    `

  const loadingStyles =
    $overrideDisabled &&
    css`
      &,
      & *,
      &:disabled,
      &:disabled * {
        cursor: wait;
      }

      & > *:not([data-button-loader]) {
        visibility: hidden;
      }
    `

  const compactStyles =
    $compact &&
    css`
      padding: ${theme.space['4']};
    `

  return css`
    position: relative;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: fit-content;
    cursor: pointer;
    overflow: initial;
    padding: 0;
    margin: 0;
    border-radius: unset;
    user-select: none;
    border: none;
    outline: none;
    background: none;
    font-weight: 500;
    gap: ${theme.space['4']};

    * {
      cursor: pointer;
    }

    &:focus-visible {
      outline: -webkit-focus-ring-color auto ${theme.border.size['1']};
    }

    &:disabled,
    &:disabled * {
      cursor: not-allowed;
    }

    > [data-button-children-wrapper] > svg {
      aspect-ratio: 1 / 1;
    }

    ${buttonSizeStyles(theme, $size, $use, $onlyBox)}
    ${appearanceStyles[$use]}
    ${onlyCircleStyles}
    ${onlyBoxStyles}
    ${loadingStyles}
    ${compactStyles}
  `
}

export const Button = styled.button<{
  $use: ButtonUseKeys
  $size: ButtonSizeKeys
  $onlyBox?: boolean
  $onlyCircle?: boolean
  $overrideDisabled?: boolean
  $compact?: boolean
}>(({ theme, $use, $size, $onlyBox, $onlyCircle, $overrideDisabled, $compact }) =>
  buttonStyles(theme, $use, $size, $onlyBox, $onlyCircle, $overrideDisabled, $compact),
)

export const ChildrenWrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.space['4']};
  `,
)
